import { useState } from 'react';
import { Button, Checkbox, Flex, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { formatStock } from '@app/shared';
import { FloatingWindow } from '@/shared/ui/FloatingWindow';
import { useWindowManager } from '@/shared/lib/windows';
import { CategoryThumb, useListCategoriesQuery } from '@/entities/category';
import type { CategoryDto } from '@/entities/category';
import { useSelectableRow } from '@/shared/lib/hooks/useSelectableRow';
import { HotkeyHint } from '@/shared/lib/hotkeys';

export interface CategoryGroupWindowProps {
  id: string;
  onClose: () => void;
}

const COLUMNS: ColumnsType<CategoryDto> = [
  { title: 'Фото', key: 'photo', width: 52, render: (_, c) => <CategoryThumb category={c} /> },
  { title: 'Название', dataIndex: 'name' },
  { title: 'N', dataIndex: 'pcsPerBox', width: 60, align: 'right' },
  { title: 'Опт. цена', dataIndex: 'wholesalePrice', width: 100, align: 'right', render: (v: string | null) => v ?? '—' },
  { title: 'Остаток', width: 140, render: (_, c) => (c.stock ? formatStock(c.stock) : '—') },
  { title: 'Новинка', dataIndex: 'isNew', width: 80, render: (v: boolean) => (v ? <Tag color="blue">новинка</Tag> : null) },
  { title: 'Акция', dataIndex: 'isPromo', width: 80, render: (v: boolean) => (v ? <Tag color="orange">акция</Tag> : null) },
  { title: 'Архив', dataIndex: 'isArchived', width: 70, render: (v: boolean) => (v ? <Tag>архив</Tag> : null) },
];

/** §2.1: плавающее окно группы — основные категории внутри корневой группы `id`, открывается
 *  двойным кликом по строке группы на `CategoriesPage`. Перемещается/растягивается как любое
 *  другое окно (`FloatingWindow`), можно держать несколько групп открытыми одновременно. */
export function CategoryGroupWindow({ id, onClose }: CategoryGroupWindowProps) {
  const windows = useWindowManager();
  const [includeArchived, setIncludeArchived] = useState(false);
  const { data = [], isFetching } = useListCategoriesQuery({ includeArchived });

  const root = data.find((c) => c.id === id);
  const mains = data.filter((c) => c.parentId === id);
  const selection = useSelectableRow(mains, {
    onOpen: (item) => windows.open({ kind: 'category', id: item.id }),
    onCreate: () => windows.open({ kind: 'categoryCreate', parentId: id }),
    createLabel: 'новая категория',
  });

  return (
    <FloatingWindow kind="categoryGroup" title={`Группа: ${root?.name ?? ''}`} onClose={onClose}>
      <Flex vertical gap={8} style={{ height: '100%' }}>
        {root ? (
          // Вход в карточку самой группы: название, фото (загрузка/замена/удаление), архив.
          <Flex gap={12} align="center">
            <CategoryThumb category={root} size={48} previewable />
            <Typography.Text strong>{root.name}</Typography.Text>
            <Button onClick={() => windows.open({ kind: 'category', id })}>Изменить группу</Button>
          </Flex>
        ) : null}

        <Flex justify="space-between" align="center">
          <Button type="primary" onClick={() => windows.open({ kind: 'categoryCreate', parentId: id })}>
            Новая категория
          </Button>
          <Checkbox checked={includeArchived} onChange={(e) => setIncludeArchived(e.target.checked)}>
            Показать архивные
          </Checkbox>
        </Flex>

        <Table<CategoryDto>
          size="small"
          rowKey="id"
          loading={isFetching}
          dataSource={mains}
          columns={COLUMNS}
          pagination={false}
          rowClassName={(c) => (c.isArchived ? 'row-draft' : '')}
          onRow={selection.rowProps}
        />

        <HotkeyHint bindings={selection.bindings} />
      </Flex>
    </FloatingWindow>
  );
}
